/**
 * utils/baseRopProcessor.js - Processeur spécialisé BaseROP
 * Validation et transformation des données BaseROP Excel avec extraction PBO
 */

import { readExcelFile, readExcelCell, getSheetColumns, extractExcelFileInfo, getSheetMaxCols } from "./excelReader.js";

import { processPboValue, validatePboBatch } from "./pboExtractor.js";

function validateReferenceContent(worksheet, columnLetter, startRow = 3, sampleSize = 5) {
  let validCount = 0;
  let totalChecked = 0;

  for (let row = startRow; row < startRow + sampleSize; row++) {
    const cell = worksheet[`${columnLetter}${row}`];
    const rawValue = readExcelCell(cell);

    if (rawValue) {
      const value = rawValue.toString().trim().toUpperCase();
      if (value) {
        totalChecked++;
        if (/^B\d+-T\d+-MODULE-[A-Z0-9]$/.test(value)) {
          validCount++;
        }
      }
    }
  }

  return totalChecked > 0 && validCount / totalChecked >= 0.8;
}

/**
 * Chercher les colonnes REFERENCE et PORT consécutives en row 2
 * @param {Object} workbook - Workbook SheetJS
 * @param {string} sheetName - Nom de la feuille
 * @returns {Object} { isValid: boolean, errors: string[], columns: Object|null }
 */
function _findConsecutiveColumns(workbook, sheetName) {
  const errors = [];

  if (!workbook || !workbook.Sheets) {
    errors.push("Workbook invalide");
    return { isValid: false, errors, columns: null };
  }

  const worksheet = workbook.Sheets[sheetName];
  if (!worksheet) {
    errors.push(`Feuille "${sheetName}" non trouvée`);
    return { isValid: false, errors, columns: null };
  }

  try {
    // Lire spécifiquement la ligne 2 (row 2 = index 1)
    const headerRow = [];

    const maxCol = getSheetMaxCols(worksheet);

    // Gérer les colonnes au-delà de Z (AA, AB, etc.) ***
    const getColumnLetter = (colIndex) => {
      let letter = "";
      let temp = colIndex;
      while (temp >= 0) {
        letter = String.fromCharCode(65 + (temp % 26)) + letter;
        temp = Math.floor(temp / 26) - 1;
      }
      return letter;
    };

    // Lire la row 2 (index 1)
    for (let col = 0; col < Math.max(maxCol, 20); col++) {
      const cellAddress = getColumnLetter(col) + "2"; // A2, B2, C2... AA2, AB2...
      const cell = worksheet[cellAddress];

      // Gérer les cellules undefined/null ***
      let cellValue = "";
      if (cell) {
        cellValue = readExcelCell(cell);
        if (typeof cellValue === "string") {
          cellValue = cellValue.trim().toUpperCase();
        } else {
          cellValue = String(cellValue).trim().toUpperCase();
        }
      }

      headerRow.push(cellValue);
    }

    // Commencer à l'index 2
    for (let i = 2; i < headerRow.length - 1; i++) {
      const current = headerRow[i];
      const next = headerRow[i + 1];

      if (current === "REFERENCE" && next === "PORT") {
        const refLetter = getColumnLetter(i);
        if (validateReferenceContent(worksheet, refLetter)) {
          return {
            isValid: true,
            errors: [],
            columns: {
              REFERENCE: {
                index: i,
                letter: getColumnLetter(i),
                name: "REFERENCE",
              },
              PORT: {
                index: i + 1,
                letter: getColumnLetter(i + 1),
                name: "PORT",
              },
            },
          };
        }
      }
    }

    // Message d'erreur plus informatif ***
    errors.push(
      `Colonnes 'REFERENCE' et 'PORT' consécutives non trouvées en ligne 2. Headers détectés: ${headerRow
        .filter((h) => h)
        .slice(0, 5)
        .join(", ")}`
    );
    return { isValid: false, errors, columns: null };
  } catch (error) {
    errors.push(`Erreur de lecture des en-têtes: ${error.message}`);
    return { isValid: false, errors, columns: null };
  }
}

/**
 * Extraire les données depuis la ligne 3 avec colonnes spécifiées
 * @param {Object} workbook - Workbook SheetJS
 * @param {string} sheetName - Nom de la feuille
 * @param {Object} columns - Colonnes {REFERENCE, PORT, BPO_REF}
 * @returns {Object} { isValid: boolean, errors: string[], data: Array }
 */
function _extractRopData(workbook, sheetName, columns) {
  const errors = [];

  if (!workbook || !workbook.Sheets) {
    errors.push("Workbook invalide");
    return { isValid: false, errors, data: [] };
  }

  const worksheet = workbook.Sheets[sheetName];
  if (!worksheet) {
    errors.push(`Feuille "${sheetName}" non trouvée`);
    return { isValid: false, errors, data: [] };
  }

  if (!columns || !columns.REFERENCE || !columns.PORT || !columns.BPO_REF) {
    errors.push("Colonnes requises manquantes (REFERENCE, PORT, BPO_REF)");
    return { isValid: false, errors, data: [] };
  }

  try {
    const data = [];
    const maxRows = 10000; // Limite de sécurité

    // Commencer à la ligne 3 (index 2)
    for (let row = 3; row <= maxRows + 2; row++) {
      // Lire les trois colonnes
      const refCell = worksheet[`${columns.REFERENCE.letter}${row}`];
      const portCell = worksheet[`${columns.PORT.letter}${row}`];
      const bpoCell = worksheet[`${columns.BPO_REF.letter}${row}`];

      // Extraire les valeurs brutes
      const refValue = readExcelCell(refCell);
      const portValue = readExcelCell(portCell);
      const rawBpoValue = readExcelCell(bpoCell);

      // *** AJOUT: Filtrage PBO robuste ***
      //const bpoValue = rawBpoValue.includes("PBO-SRO-BPI") || rawBpoValue.includes("BE-SRO-BPI") ? rawBpoValue : "";
      const bpoValue = rawBpoValue.trim().toUpperCase();

      // Vérifier si on a atteint la fin des données
      if (!refValue && !portValue && !bpoValue) {
        // Si on a 5 lignes consécutives vides, on s'arrête
        let emptyCount = 0;
        for (let checkRow = row; checkRow < row + 5; checkRow++) {
          // *** CORRECTION: Utiliser readExcelCell ***
          const checkRef = readExcelCell(worksheet[`${columns.REFERENCE.letter}${checkRow}`]);
          const checkPort = readExcelCell(worksheet[`${columns.PORT.letter}${checkRow}`]);
          const checkBpo = readExcelCell(worksheet[`${columns.BPO_REF.letter}${checkRow}`]);

          if (!checkRef && !checkPort && !checkBpo) {
            emptyCount++;
          }
        }
        if (emptyCount >= 50) break;
        continue; // Ligne vide mais on continue
      }

      // Skip les valeurs N/A et les cellules vides
      if (isEmptyOrNA(refValue) && isEmptyOrNA(portValue) && isEmptyOrNA(bpoValue)) {
        continue;
      }

      // On garde la ligne si au moins une valeur est valide
      if (!isEmptyOrNA(refValue) || !isEmptyOrNA(portValue) || !isEmptyOrNA(bpoValue)) {
        data.push({
          EXCEL_ROW: row,
          REFERENCE_RAW: refValue,
          PORT_RAW: portValue,
          BPO_RAW: bpoValue, // Valeur filtrée
          // Valeurs nettoyées
          REFERENCE: refValue || "",
          PORT: portValue || "",
          BPO_REF: bpoValue || "",
        });
      }
    }

    return {
      isValid: true,
      errors: [],
      data: data,
    };
  } catch (error) {
    errors.push(`Erreur d'extraction des données: ${error.message}`);
    return { isValid: false, errors, data: [] };
  }
}

/**
 * Vérifier si une valeur est vide ou N/A
 * @param {string} value - Valeur à tester
 * @returns {boolean} true si vide ou N/A
 */
function isEmptyOrNA(value) {
  if (!value || typeof value !== "string") return true;
  const cleaned = value.trim().toUpperCase();
  return cleaned === "" || cleaned === "N/A" || cleaned === "NA" || cleaned === "#N/A";
}

/**
 * Valider les données BaseROP extraites
 * @param {Array} data - Données extraites
 * @returns {Object} { isValid: boolean, errors: string[], warnings: string[], stats: Object }
 */
function _validateRopData(data) {
  const errors = [];
  const warnings = [];

  if (!data || !Array.isArray(data)) {
    errors.push("Données invalides");
    return { isValid: false, errors, warnings, stats: {} };
  }

  if (data.length === 0) {
    errors.push("Aucune donnée à traiter");
    return { isValid: false, errors, warnings, stats: {} };
  }

  // Validation par échantillon (100 premières lignes)
  const sampleSize = Math.min(100, data.length);
  const sampleData = data.slice(0, sampleSize);

  let emptyReferences = 0;
  let invalidPorts = 0;
  let emptyBpos = 0;
  const references = new Set();
  const duplicateReferences = new Set();

  // Extraire tous les BPO pour validation
  const bpoValues = sampleData.map((row) => row.BPO_REF).filter((bpo) => bpo && !isEmptyOrNA(bpo));

  const bpoValidation = validatePboBatch(bpoValues);

  sampleData.forEach((row) => {
    const { REFERENCE, PORT, BPO_REF } = row;

    // Validation REFERENCE
    if (isEmptyOrNA(REFERENCE)) {
      emptyReferences++;
    } else {
      const refTrimmed = REFERENCE.trim();
      if (references.has(refTrimmed)) {
        duplicateReferences.add(refTrimmed);
      } else {
        references.add(refTrimmed);
      }
    }

    // Validation PORT
    if (isEmptyOrNA(PORT)) {
      invalidPorts++;
    } else {
      const portNum = parseInt(PORT);
      if (isNaN(portNum) || portNum < 1 || portNum > 288) {
        invalidPorts++;
      }
    }

    // Validation BPO_REF
    if (isEmptyOrNA(BPO_REF)) {
      emptyBpos++;
    }
  });

  // Calculs de pourcentages
  const emptyRefPercent = (emptyReferences / sampleSize) * 100;
  const invalidPortPercent = (invalidPorts / sampleSize) * 100;
  const emptyBpoPercent = (emptyBpos / sampleSize) * 100;
  const invalidBpoPercent = ((bpoValues.length - bpoValidation.valid) / bpoValues.length) * 100;

  // Warnings
  if (emptyRefPercent > 5) {
    warnings.push(`${emptyRefPercent.toFixed(1)}% des références sont vides`);
  }

  if (invalidPortPercent > 5) {
    warnings.push(`${invalidPortPercent.toFixed(1)}% des ports sont invalides`);
  }

  if (emptyBpoPercent > 10) {
    warnings.push(`${emptyBpoPercent.toFixed(1)}% des BPO sont vides`);
  }

  if (invalidBpoPercent > 20) {
    warnings.push(`${invalidBpoPercent.toFixed(1)}% des BPO ont un format invalide`);
  }

  if (duplicateReferences.size > 0) {
    warnings.push(`${duplicateReferences.size} références dupliquées détectées`);
  }

  // Erreurs critiques
  if (emptyRefPercent > 50) {
    errors.push("Trop de références manquantes (>50%)");
  }

  if (invalidPortPercent > 50) {
    errors.push("Trop de ports invalides (>50%)");
  }

  if (invalidBpoPercent > 80) {
    errors.push("Trop de BPO avec format invalide (>80%)");
  }

  const stats = {
    totalRows: data.length,
    sampleSize: sampleSize,
    validReferences: sampleSize - emptyReferences,
    validPorts: sampleSize - invalidPorts,
    validBpos: sampleSize - emptyBpos,
    uniqueReferences: references.size,
    duplicateReferences: duplicateReferences.size,
    bpoValidation: bpoValidation,
  };

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    stats,
  };
}

/**
 * Transformer les données BaseROP avec traitement PBO
 * @param {Array} rawData - Données brutes extraites
 * @param {Object} columns - Colonnes utilisées
 * @param {File} file - Fichier original
 * @param {string} sheetName - Nom de la feuille
 * @returns {Object} Données transformées
 */
function _transformRopData(rawData, columns, file, sheetName) {
  if (!rawData || !Array.isArray(rawData)) {
    throw new Error("transformRopData: données invalides");
  }

  // Transformer chaque ligne avec traitement PBO
  const transformedReferences = rawData
    .map((row, index) => {
      const { EXCEL_ROW, REFERENCE_RAW, PORT_RAW, BPO_RAW } = row;

      // Traitement des valeurs
      const reference = REFERENCE_RAW ? REFERENCE_RAW.trim() : "";
      const portNum = PORT_RAW ? parseInt(PORT_RAW.trim()) || 0 : 0;

      // Traitement PBO avec l'extracteur
      const pboResult = processPboValue(BPO_RAW);

      return {
        // Données principales
        REFERENCE: reference,
        PORT: portNum,
        PBO_RAW: BPO_RAW || "",

        // PBO traité selon le type
        PBO_TYPE: pboResult.type,
        PBO_VALUE: pboResult.value,
        PBO_NORMALIZED: pboResult.normalized,
        PBO_IS_VALID: pboResult.isValid,
        PBO_ERROR: pboResult.error,

        // Debug & traçabilité
        EXCEL_ROW: EXCEL_ROW,
        SHEET_NAME: sheetName,

        // Valeurs brutes pour debug
        REFERENCE_RAW: REFERENCE_RAW || "",
        PORT_RAW: PORT_RAW || "",

        // Métadonnées
        PROCESSED_AT: new Date().toISOString(),
        INDEX: index,
      };
    })
    .filter((ref) => {
      if (!ref.PBO_IS_VALID) return false;
      // Filtrer les lignes avec au moins une donnée valide
      return ref.REFERENCE || ref.PORT > 0 || ref.PBO_IS_VALID;
    });

  // Calculer les statistiques
  const validPbos = transformedReferences.filter((r) => r.PBO_IS_VALID);
  const pboByType = validPbos.reduce((acc, ref) => {
    acc[ref.PBO_TYPE] = (acc[ref.PBO_TYPE] || 0) + 1;
    return acc;
  }, {});

  const stats = {
    totalProcessed: transformedReferences.length,
    totalOriginal: rawData.length,
    filteredOut: rawData.length - transformedReferences.length,

    // Stats références
    validReferences: transformedReferences.filter((r) => r.REFERENCE).length,
    uniqueReferences: new Set(transformedReferences.map((r) => r.REFERENCE).filter((r) => r)).size,

    // Stats ports
    validPorts: transformedReferences.filter((r) => r.PORT > 0).length,
    portRange: {
      min: Math.min(...transformedReferences.map((r) => r.PORT).filter((p) => p > 0)) || 0,
      max: Math.max(...transformedReferences.map((r) => r.PORT)) || 0,
    },

    // Stats PBO
    validPbos: validPbos.length,
    invalidPbos: transformedReferences.length - validPbos.length,
    pboByType: pboByType,
  };

  // Informations du fichier
  const fileInfo = extractExcelFileInfo(file);

  return {
    // Informations fichier
    fileName: file.name,
    fileSize: file.size,
    fileSizeFormatted: fileInfo.sizeFormatted,
    fileLastModified: file.lastModified,
    fileCreationDate: fileInfo.lastModifiedFormatted,

    // Métadonnées de processing
    metadata: {
      sheetName: sheetName,
      columnsUsed: columns,
      validatedAt: new Date().toISOString(),
      processingTimestamp: Date.now(),
    },

    // Données transformées
    references: transformedReferences,
    referenceCount: transformedReferences.length,

    // Statistiques
    stats: stats,

    // Colonnes utilisées
    columns: columns,
  };
}

/**
 * Traitement complet d'un fichier BaseROP
 * @param {File} file - Fichier Excel BaseROP
 * @param {string} selectedSheet - Nom de la feuille sélectionnée
 * @param {Object} bpoColumn - Colonne BPO_REF sélectionnée par l'utilisateur
 * @returns {Promise<Object>} Résultat de validation et traitement
 */
export async function processBaseRopFile(file, selectedSheet, bpoColumn) {
  try {
    // 1. Lire le fichier Excel
    const excelResult = await readExcelFile(file);
    if (!excelResult.isValid) {
      return {
        isValid: false,
        errors: excelResult.errors,
        data: null,
      };
    }

    // 2. Trouver les colonnes REFERENCE et PORT consécutives
    const columnsResult = _findConsecutiveColumns(excelResult.workbook, selectedSheet);
    if (!columnsResult.isValid) {
      return {
        isValid: false,
        errors: ["Colonnes REFERENCE/PORT non trouvées:", ...columnsResult.errors],
        data: null,
      };
    }

    // 3. Construire l'objet colonnes complet
    const columns = {
      REFERENCE: columnsResult.columns.REFERENCE,
      PORT: columnsResult.columns.PORT,
      BPO_REF: bpoColumn,
    };

    // 4. Extraire les données depuis la ligne 3
    const extractResult = _extractRopData(excelResult.workbook, selectedSheet, columns);

    if (!extractResult.isValid) {
      const errors = Array.isArray(extractResult.errors) ? extractResult.errors : [extractResult.errors];
      return {
        isValid: false,
        errors: ["Erreur d'extraction des données:", ...errors],
        data: null,
      };
    }

    // 5. Valider les données extraites
    const validation = _validateRopData(extractResult.data);
    if (!validation.isValid) {
      return {
        isValid: false,
        errors: ["Validation des données échouée:", ...validation.errors],
        warnings: validation.warnings,
        data: null,
      };
    }

    // 6. Transformer les données avec traitement PBO

    const transformedData = _transformRopData(extractResult.data, columns, file, selectedSheet);

    return {
      isValid: true,
      errors: [],
      warnings: validation.warnings || [],
      data: transformedData,
      stats: validation.stats,
      columns: columns,
    };
  } catch (error) {
    return {
      isValid: false,
      errors: [`Erreur de traitement BaseROP: ${error.message}`],
      data: null,
    };
  }
}

/**
 * Analyser un fichier BaseROP pour détecter automatiquement les colonnes
 * @param {File} file - Fichier Excel
 * @param {string} sheetName - Nom de la feuille à analyser
 * @returns {Promise<Object>} Analyse avec colonnes détectées
 */
export async function analyzeBaseRopFile(file, sheetName) {
  try {
    // Lire le fichier
    const excelResult = await readExcelFile(file);
    if (!excelResult.isValid) {
      return {
        isValid: false,
        errors: excelResult.errors,
        analysis: null,
      };
    }

    // Détecter REFERENCE et PORT
    const columnsResult = _findConsecutiveColumns(excelResult.workbook, sheetName);

    // Obtenir toutes les colonnes pour le choix BPO_REF
    const allColumnsResult = getSheetColumns(excelResult.workbook, sheetName, {
      headerRow: 2, // Headers en ligne 2
      sampleRows: 10,
    });

    return {
      isValid: true,
      errors: [],
      analysis: {
        autoDetectedColumns: columnsResult.isValid ? columnsResult.columns : null,
        allColumns: allColumnsResult.isValid ? allColumnsResult.columns : [],
        detectionErrors: columnsResult.errors,
        sheetInfo: {
          name: sheetName,
          hasValidStructure: columnsResult.isValid && allColumnsResult.isValid,
        },
      },
    };
  } catch (error) {
    return {
      isValid: false,
      errors: [`Erreur d'analyse: ${error.message}`],
      analysis: null,
    };
  }
}
